import React, { useState, useEffect, useRef, useCallback } from 'react';
import TutorialButton from './common/TutorialButton'; // Import TutorialButton
import { TabulatorFull as Tabulator } from 'tabulator-tables';
import api from '../services/api';
import { tratarErroAPI, logarRequisicao, logarSucesso } from '../utils/errorHandler';
import '../styles/tipoambiente.css';
import etgLogo from '../assets/etg.png';
import ufmgLogo from '../assets/Ufmg-sem nome-03.png';
import '../styles/home.css';

const EstadoManager = () => {
  const [dados, setDados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mensagem, setMensagem] = useState({ tipo: '', texto: '' });
  const [formData, setFormData] = useState({
    id_sigla_est: '',
    num_estado: '',
    nome_estado: '',
    geom_limite: ''
  });
  const [apiStatus, setApiStatus] = useState('checking');
  const [lastUpdate, setLastUpdate] = useState(null);
  const [camposComErro, setCamposComErro] = useState([]);

  const tabulatorRef = useRef(null);
  const tableRef = useRef(null);

  const mostrarMensagem = useCallback((tipo, texto) => {
    setMensagem({ tipo, texto });
    setTimeout(() => setMensagem({ tipo: '', texto: '' }), 4000);
  }, []);

  const verificarAPI = useCallback(async () => {
    try {
      setApiStatus('checking');
      await api.get('/estado');
      setApiStatus('connected');
    } catch (error) {
      setApiStatus('error');
      mostrarMensagem('error', `Erro de conexão ou permissão: ${error.message}`);
    }
  }, [mostrarMensagem]);

  useEffect(() => {
    verificarAPI();
  }, [verificarAPI]);

  const atualizarRegistro = useCallback(async (id, dadosAtualizacao) => {
    logarRequisicao('Atualizar', 'estado', { id, dadosAtualizacao });
    try {
      const response = await api.put(`/estado/${id}`, dadosAtualizacao);
      logarSucesso('Atualizar', 'estado', response.data);
      if (response && response.data) {
        if (tabulatorRef.current) {
          tabulatorRef.current.updateData([response.data.data]);
        }
        mostrarMensagem('success', 'Registro atualizado com sucesso!');
      } else {
        mostrarMensagem('error', 'Resposta da API inválida ao atualizar.');
      }
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'atualizar', 'estado');
      mostrarMensagem('error', mensagemErro);
    }
  }, [mostrarMensagem]);

  const excluirRegistro = useCallback(async (id) => {
    logarRequisicao('Excluir', 'estado', { id });
    try {
      const response = await api.delete(`/estado/${id}`);
      logarSucesso('Excluir', 'estado', response.data);
      setDados(currentData => currentData.filter(item => item.id_sigla_est !== id));
      mostrarMensagem('success', 'Registro excluído com sucesso!');
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'excluir', 'estado');
      mostrarMensagem('error', mensagemErro);
    }
  }, [mostrarMensagem]);

  const getColunas = useCallback(() => {
    const colunasBase = [
      {
        title: 'Sigla',
        field: 'id_sigla_est',
        width: 100,
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar Sigla...',
        sorter: 'string'
      },
      {
        title: 'Número',
        field: 'num_estado',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar número...',
        sorter: 'number',
      },
      {
        title: 'Nome',
        field: 'nome_estado',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar nome...',
        sorter: 'string',
      },
      {
        title: 'Geometria',
        field: 'geom_limite',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar geometria...',
        sorter: 'string',
      }
    ];

    return [
      {
        title: 'Salvar',
        field: 'salvar',
        width: 80,
        hozAlign: 'center',
        formatter: () => '<button class="btn-save"><i class="fas fa-save"></i></button>',
        cellClick: (e, cell) => {
          const data = cell.getRow().getData();
          atualizarRegistro(data.id_sigla_est, data);
        },
        headerSort: false
      },
      {
        title: 'Excluir',
        field: 'excluir',
        width: 80,
        hozAlign: 'center',
        formatter: () => '<button class="btn-delete"><i class="fas fa-trash"></i></button>',
        cellClick: (e, cell) => {
          const data = cell.getRow().getData();
          if (window.confirm(`Deseja realmente excluir o registro "${data.id_sigla_est}"?`)) {
            excluirRegistro(data.id_sigla_est);
          }
        },
        headerSort: false
      },
      ...colunasBase.map(col => ({
        ...col,
        editor: col.field === 'id_sigla_est' ? undefined : 'input'
      }))
    ];
  }, [atualizarRegistro, excluirRegistro]);

  const carregarDados = useCallback(async (showMessage = true) => {
    try {
      setLoading(true);
      const response = await api.get('/estado');
      setDados(response.data.data || []);
      setLastUpdate(new Date());
      if (showMessage) {
        mostrarMensagem('success', `${response.data.data?.length || 0} registros carregados`);
      }
    } catch (error) {
      mostrarMensagem('error', error.message);
      setDados([]);
    } finally {
      setLoading(false);
    }
  }, [mostrarMensagem]);

  useEffect(() => {
    if (apiStatus === 'connected') {
      carregarDados(false);
    }
  }, [apiStatus, carregarDados]);

  const inicializarTabulator = useCallback(() => {
    if (!tableRef.current) return;
    if (tabulatorRef.current) tabulatorRef.current.destroy();

    tabulatorRef.current = new Tabulator(tableRef.current, {
      data: dados,
      columns: getColunas(),
      layout: 'fitColumns',
      pagination: 'local',
      paginationSize: 10,
      paginationCounter: 'rows',
      movableColumns: true,
      resizableRows: true,
      tooltips: true,
      placeholder: 'Nenhum registro encontrado',
      index: 'id_sigla_est'
    });

    const style = document.createElement('style');
    style.textContent = `
      .btn-save {
        background: #007bff;
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 3px;
        cursor: pointer;
        font-size: 12px;
      }
      .btn-save:hover {
        background: #0056b3;
      }
      .btn-delete {
        background: #dc3545;
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 3px;
        cursor: pointer;
        font-size: 12px;
      }
      .btn-delete:hover {
        background: #c82333;
      }
      .btn-back {
        background: transparent;
        border: none;
        font-size: 24px;
        cursor: pointer;
      }
    `;
    document.head.appendChild(style);
  }, [dados, getColunas]);

  useEffect(() => {
    if (apiStatus === 'connected' && tableRef.current) {
      if (!tabulatorRef.current) {
        inicializarTabulator();
      } else {
        tabulatorRef.current.setColumns(getColunas());
        tabulatorRef.current.setData(dados);
      }
    }
  }, [dados, apiStatus, inicializarTabulator, getColunas]);

  const adicionarRegistro = async (e) => {
    e.preventDefault();

    // Validação de campos obrigatórios com mensagens específicas
    const camposObrigatorios = [];
    const erros = [];

    if (!formData.id_sigla_est.trim()) {
      camposObrigatorios.push('Sigla');
      erros.push('id_sigla_est');
    }
    if (!formData.num_estado.trim()) {
      camposObrigatorios.push('Número');
      erros.push('num_estado');
    }
    if (!formData.nome_estado.trim()) {
      camposObrigatorios.push('Nome');
      erros.push('nome_estado');
    }

    if (camposObrigatorios.length > 0) {
      setCamposComErro(erros);
      mostrarMensagem('error', `Por favor, preencha os campos obrigatórios: ${camposObrigatorios.join(', ')}`);
      return;
    }

    setCamposComErro([]);

    logarRequisicao('Adicionar', 'estado', formData);
    try {
      setLoading(true);
      const response = await api.post('/estado', formData);
      logarSucesso('Adicionar', 'estado', response.data);
      setDados([...dados, response.data.data]);
      setFormData({ id_sigla_est: '', num_estado: '', nome_estado: '', geom_limite: '' });
      setCamposComErro([]);
      mostrarMensagem('success', 'Registro adicionado com sucesso!');
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'adicionar', 'estado');
      mostrarMensagem('error', mensagemErro);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Remove o erro do campo quando ele for preenchido
    if (value) {
      setCamposComErro(prev => prev.filter(campo => campo !== name));
    }
  };

  const exportarDados = () => {
    if (tabulatorRef.current) {
      tabulatorRef.current.download('csv', 'estados.csv');
    }
  };

  return (
    <div className="tipo-ambiente-container">
      <div className="header-controls">
        <button className="btn-back" onClick={() => window.history.back()}><i class="fas fa-arrow-left"></i></button>
      </div>

      <div className="header">
        <div className="title-container">
          <img src={etgLogo} alt="ETG Logo" className="logo" />
          <h1>Gerenciador de Estados</h1>
          <img src={ufmgLogo} alt="UFMG Logo" className="logo" />
        </div>
        <p>Interface para popular e gerenciar a tabela de estados</p>
        <div style={{ marginTop: '10px' }}>
          <TutorialButton videoPath="bdados/estados.mov" buttonText="Ver Tutorial Estados" />
        </div>
      </div>

      {mensagem.texto && (
        <div className="status-container">
          <div className={`status ${mensagem.tipo}`}>{mensagem.texto}</div>
        </div>
      )}


      <div className="controls">
        <form onSubmit={adicionarRegistro} className="form-inline">
          <div className="form-group">
            <label htmlFor="id_sigla_est">Sigla:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
            <input
              type="text"
              id="id_sigla_est"
              name="id_sigla_est"
              value={formData.id_sigla_est}
              onChange={handleInputChange}
              placeholder="Ex: SP"
              maxLength={2}
              disabled={loading || apiStatus !== 'connected'}
              className={camposComErro.includes('id_sigla_est') ? 'input-error' : ''}
            />
          </div>

          <div className="form-group">
            <label htmlFor="num_estado">Número:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
            <input
              type="number"
              id="num_estado"
              name="num_estado"
              value={formData.num_estado}
              onChange={handleInputChange}
              placeholder="Ex: 35"
              disabled={loading || apiStatus !== 'connected'}
              className={camposComErro.includes('num_estado') ? 'input-error' : ''}
            />
          </div>

          <div className="form-group">
            <label htmlFor="nome_estado">Nome:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
            <input
              type="text"
              id="nome_estado"
              name="nome_estado"
              value={formData.nome_estado}
              onChange={handleInputChange}
              placeholder="Ex: São Paulo"
              maxLength={255}
              disabled={loading || apiStatus !== 'connected'}
              className={camposComErro.includes('nome_estado') ? 'input-error' : ''}
            />
          </div>

          <div className="form-group">
            <label htmlFor="geom_limite">Geometria (WKT):</label>
            <textarea
              id="geom_limite"
              name="geom_limite"
              value={formData.geom_limite}
              onChange={handleInputChange}
              placeholder="Ex: POLYGON((...))"
              disabled={loading || apiStatus !== 'connected'}
            />
          </div>

          <div className="form-actions-row">
            <div className="form-buttons">
              <button type="submit" className="btn btn-success" disabled={loading || apiStatus !== 'connected'}>
                {loading ? '⏳ Adicionando...' : '➕ Adicionar'}
              </button>
              <button className="btn btn-primary" onClick={() => carregarDados(true)} disabled={loading || apiStatus !== 'connected'}>
                {loading ? '⏳ Carregando...' : '🔄 Recarregar'}
              </button>
              <button className="btn btn-secondary" onClick={exportarDados} disabled={dados.length === 0}>📥 Exportar CSV</button>
            </div>

            <div className="info-campos-obrigatorios">
              <i className="fas fa-info-circle"></i>
              <span>* indica que os campos são obrigatorios.</span>
            </div>
          </div>
        </form>
      </div>


      <div className="stats-bar">
        <div className="stats-item"><strong>{dados.length}</strong> registros carregados</div>
        <div className="stats-item">Última atualização: <strong>{lastUpdate ? lastUpdate.toLocaleTimeString() : 'N/A'}</strong></div>
      </div>

      <div className="tabulator-container">
        <div ref={tableRef}></div>
      </div>

    </div>
  );
};

export default EstadoManager;